/**
 * Die Aura-Level: Namen an EINER Stelle, dazu das angezeigte Level.
 *
 * Aura v2 (Okt 2026): sechs Wellen statt fuenf Titel. Die Namen kommen aus
 * constants/otterLevels.js (sie gehoeren zu den Wellen der Otter-Figur). Die
 * alten Titel (Anfaenger ... Weiser) waren generisches Maskulinum und sind raus.
 *
 * Die Punkte-Schwellen stehen NICHT hier: Die kommen pro Schule aus
 * GET /api/v1/aura/stats (`thresholds`) bzw. /aura/config (Fallback:
 * components/systems/AuraSystem.js).
 */
import { OTTER_LEVEL_NAMES, OTTER_MAX_LEVEL } from './otterLevels.js';
import { getAuraLevelProgress } from '../utils/auraUtils';
import { AURA_CONFIG } from '../components/systems/AuraSystem';

export const MAX_LEVEL = OTTER_MAX_LEVEL;

export const LEVEL_NAMES = OTTER_LEVEL_NAMES.reduce((acc, name, index) => {
  acc[index + 1] = name;
  return acc;
}, {});

const clampLevel = (level, max = MAX_LEVEL) => {
  const n = Number.parseInt(level, 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(Math.max(n, 1), max);
};

export const getLevelName = (level) => LEVEL_NAMES[clampLevel(level)] || `Level ${level}`;

const positiveLevel = (value) => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 1 ? n : 0;
};

/**
 * Angezeigtes Level: das hoechste je erreichte (peakLevel), mindestens `level`.
 * Wie `displayLevel` im Backend (backend/src/constants/auraDefaults.js). Ohne
 * beides 1.
 */
export const resolveDisplayLevel = ({ peakLevel, level } = {}) => (
  Math.min(Math.max(positiveLevel(peakLevel), positiveLevel(level), 1), MAX_LEVEL)
);

/** Stufen einer Schwellen-Tabelle, aufsteigend; ungueltig -> Standard-Leiter */
export const thresholdLevels = (thresholds) => {
  const valid = (source) => Object.keys(source || {})
    .map(Number)
    .filter((l) => Number.isFinite(l) && Number.isFinite(Number(source[l])))
    .sort((a, b) => a - b);
  const own = thresholds && typeof thresholds === 'object' ? valid(thresholds) : [];
  if (own.length) return { levels: own, source: thresholds };
  return { levels: valid(AURA_CONFIG.levelThresholds), source: AURA_CONFIG.levelThresholds };
};

/**
 * Level + Fortschritt fuer die Anzeige.
 *
 * Wie `getAuraLevelProgress`, aber das Level ist das hoechste je erreichte.
 * Liegen die Punkte darunter (z. B. nach einer Korrektur), steht der Balken
 * auf 0 % und die Restpunkte zaehlen bis zur Stufe ueber dem angezeigten
 * Level. Nie ueber die hoechste Stufe der aktiven Leiter hinaus.
 *
 * @param {number} points - currentPoints (die angezeigte Aura)
 * @param {Object} [thresholds] - Schwellen { 1: 0, 2: 150, ... }
 * @param {number} [peakLevel] - hoechstes je erreichtes Level
 */
export const getDisplayLevelProgress = (points, thresholds, peakLevel) => {
  const safePoints = Number.isFinite(Number(points)) && Number(points) > 0 ? Number(points) : 0;
  const { levels, source } = thresholdLevels(thresholds);
  const base = getAuraLevelProgress(safePoints, source);
  const peak = positiveLevel(peakLevel);
  const reachable = levels.filter((l) => l <= peak);
  const currentLevel = reachable.length ? reachable[reachable.length - 1] : 0;
  if (currentLevel <= base.currentLevel) return base;

  const currentThreshold = Number(source[currentLevel]);
  const nextLevel = levels.find((l) => l > currentLevel) || null;
  if (!nextLevel) {
    return {
      currentLevel,
      nextLevel: null,
      currentThreshold,
      nextThreshold: null,
      pointsInLevel: 0,
      pointsToNextLevel: 0,
      progressPercentage: 100,
      isMaxLevel: true,
    };
  }
  const nextThreshold = Number(source[nextLevel]);
  return {
    currentLevel,
    nextLevel,
    currentThreshold,
    nextThreshold,
    pointsInLevel: 0,
    pointsToNextLevel: Math.max(0, nextThreshold - safePoints),
    progressPercentage: 0,
    isMaxLevel: false,
  };
};
